import configJSON from 'config.json';
import { CountryCodeList } from 'types/countries';
import { SiteLanguage, Namespaces } from 'types/locales';
import { ROOT_ROUTE } from 'components/dynamic-page/route-matchers/types';
import { NullConsentRule } from 'domain/consent';
import { AGNT_INC_BASE_URL } from 'constants/domains';
import {
  AGNT_INC_TENANT_ID,
  type AGNT_INC_TENANT_ID_TYPE,
  type ContentHosts,
  type Tenant,
  type TenantConfigHeader,
  type TenantConfigFooter,
  type TenantConfigHeadTags,
  type TenantConfigOrganization,
} from '@tenants/types';
import { createLanguageConfig } from '@tenants/utils/configs';
import { createCurrencyMap } from '@tenants/utils/currency-map';
import AgntIncTheme from './theme-config';
import { createHeaderConfig } from './header-config';
import { createFooterConfig } from './footer-config';
import { createHeadTagsConfig } from './head-tags-config';
import { createOrganizationConfig } from './organization-config';
import { homeRouteHandler } from './route-handler';

export type AgntIncTenantConfig = TenantConfigHeader & TenantConfigFooter & TenantConfigHeadTags & TenantConfigOrganization;
export type AgntIncTenant = Tenant<AGNT_INC_TENANT_ID_TYPE, typeof AgntIncTheme, AgntIncTenantConfig>;

export const agntIncTenant: AgntIncTenant = {
  id: AGNT_INC_TENANT_ID,
  // Falls back to an empty list when the environment's config.json predates the
  // agntInc tenant, so host resolution stays an array instead of throwing.
  hosts: (configJSON.hosts[AGNT_INC_TENANT_ID] as string[] | undefined) ?? [],
  // contentHosts powers the WP content page proxy (Phase 2 / local visual testing).
  // Points directly to the WPEngine origin — no nginx bypass needed in Next.js SSR.
  contentHosts: configJSON.contentPageApi[AGNT_INC_TENANT_ID] as ContentHosts,
  theme: AgntIncTheme,
  localization: {
    ...createLanguageConfig({
      globalLanguages: [SiteLanguage.English],
      standardLanguages: [SiteLanguage.English],
      globalDefaultLanguage: SiteLanguage.English,
      standardDefaultLanguage: SiteLanguage.English,
      namespaces: [Namespaces.COMMON],
      allowGlobalLocaleSwitching: false,
    }),
    countries: [CountryCodeList.UNITED_STATES],
    defaultCountry: CountryCodeList.UNITED_STATES,
    currencyMap: createCurrencyMap([CountryCodeList.UNITED_STATES]),
  },
  config: {
    header: createHeaderConfig(),
    footer: createFooterConfig(),
    headTags: createHeadTagsConfig(),
    organization: createOrganizationConfig(),
  },
  routes: {
    claimed: [ROOT_ROUTE],
    excluded: [],
    handlers: [
      { match: ROOT_ROUTE, handler: homeRouteHandler },
    ],
  },
  debug: {
    forceTenantHeaderValue: `useTenant="${AGNT_INC_TENANT_ID}"`,
    forceTenantFeatureValue: AGNT_INC_TENANT_ID,
  },
  features: {
    MyLinkMyLead: false,
    AgentSearch: false,
    AccessibilityWidget: false,
    ShowQuebecPopup: false,
    TrustArc: {
      enabled: true,
      // TODO(agnt.inc migration): switch to 'agnt.inc' once TrustArc has registered the
      // new consent profile. This value is the TrustArc profile key sent to
      // consent.trustarc.com/notice?domain=… — changing it before registration breaks the banner.
      domain: 'agntinc.com',
    },
  },
  googleTagManager: {
    containerIds: {
      defaultContainerId: configJSON.googleTagManager.containerIdAgntInc,
    },
  },
  sso: {
    googleWebClientId: '',
    facebookAppId: '',
  },
  sitemap: {
    urls: [`${AGNT_INC_BASE_URL}/api/agnt-sitemap.xml`],
  },
  robots: {
    disallowDomains: [],
    disallowPaths: [],
  },
  pvr: {
    phoneNumber: '',
  },
  mobileAppBanner: null,
  consent: NullConsentRule,  // IR site — no lead generation, no TCPA consent needed
};
